import mainStyle from './mainStyle.module.scss';

import { useState, useEffect } from 'react';

import { Box, Checkbox, IconButton, Tooltip, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as CheckBoxDefault } from 'assets/images/icons/checkbox_default.svg';
import { ReactComponent as CheckClearIcon } from 'assets/images/icons/checkbox_clear.svg';
import { ReactComponent as CheckBoxFilled } from 'assets/images/icons/checkbox_fiilled.svg';
import { ReactComponent as WriteIcon } from 'assets/images/icons/icn_table_cell_Pencil_Writing.svg';
import { ReactComponent as WriteHoverIcon } from 'assets/images/icons/icn_table_cell_Pencil_Writing_hover.svg';
import { ReactComponent as LaunchIcon } from 'assets/images/icons/icn_table_cell_Launch.svg';
import { ReactComponent as LaunchHoverIcon } from 'assets/images/icons/icn_table_cell_Launch_hover.svg';
import { ReactComponent as ProIcon } from 'assets/images/icons/icn-pro.svg';
import { ReactComponent as ProOrangeOutlineIcon } from 'assets/images/icons/icn_Pro_orange_outline.svg';

const CaptivePortalTable = ({
  tableData,
  setTableData,
  hasChecked,
  setHasChecked,
  setIsSelectedCount,
  selectedRowItem,
  setSelectedRowItem,
  setDrawerOpen,
  currentPage,
  rowsPerPage,
  setOpenEditorModal,
  showEditGeneral,
  setSelectedProfileItem,
  isUnityPro,
}) => {
  const theme = useTheme();

  const [allChecked, setAllChecked] = useState(false);

  const renderLabel = (label, type) => {
    const authText = label || '';
    const shouldShowIcon = type && type !== 'CLICK_THROUGH' && type !== 'USERNAME_PASSWORD';
    if (shouldShowIcon) {
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <span>{authText}</span>

          {isUnityPro ? (
            <ProIcon />
          ) : (
            <Tooltip
              title="Pro feature disabled."
              arrow
              placement="bottom"
              componentsProps={{
                tooltip: { sx: { bgcolor: '#4D4D4D', color: '#ffffff', px: 1.5, py: 0.5, borderRadius: '6px' } },
                arrow: { sx: { color: '#4D4D4D' } },
              }}
            >
              <ProOrangeOutlineIcon />
            </Tooltip>
          )}
        </Box>
      );
    }

    return authText;
  };

  const handleChangeCheckbox = (key, isChecked, isSelectedId) => {
    if (key !== 'CHECK_ALL') {
      setTableData(prev => prev.map(item => (item.id === isSelectedId ? { ...item, checked: isChecked } : item)));
    } else {
      setTableData(prev => prev.map(item => ({ ...item, checked: isChecked })));
    }
  };

  const handleClickRow = item => {
    setDrawerOpen(true);
    setSelectedRowItem(item);
  };

  useEffect(() => {
    if (tableData?.length > 0) {
      setAllChecked(tableData.every(row => row.checked));
      setHasChecked(tableData.some(row => row.checked));
      setIsSelectedCount(tableData.filter(item => item.checked)?.length || 0);
    } else {
      setAllChecked(false);
      setHasChecked(false);
      setIsSelectedCount(0);
      setSelectedRowItem(null);
    }
  }, [tableData]);

  return (
    <TableContainer className={mainStyle['custom-scrollbar']}>
      <Table>
        <TableHead
          sx={{
            '& th': {
              color: '#aaaaaa',
              fontSize: '15px',
              fontWeight: '500',
            },
            borderTop: 'none',
            borderColor: '1px solid #d5d5d5',
          }}
        >
          <TableRow sx={{ backgroundColor: '#ffffff' }}>
            {showEditGeneral && (
              <TableCell
                sx={{
                  '&:not(:last-of-type)::after': {
                    display: 'none',
                  },
                }}
              >
                <Checkbox
                  icon={hasChecked ? <CheckClearIcon style={{ width: '24px', height: '24px' }} /> : <CheckBoxDefault style={{ width: '24px', height: '24px' }} />}
                  checkedIcon={<CheckBoxFilled style={{ width: '24px', height: '24px' }} />}
                  checked={allChecked}
                  onChange={e => handleChangeCheckbox('CHECK_ALL', e.target.checked)}
                  sx={{
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                />
              </TableCell>
            )}

            <TableCell>
              <Typography className={mainStyle['table-header']}>Captive Portal Name</Typography>
            </TableCell>
            <TableCell>
              <Typography className={mainStyle['table-header']}>Authentication Type</Typography>
            </TableCell>
            <TableCell>
              <Typography className={mainStyle['table-header']}>Captive Portal Page</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData?.length > 0 ? (
            tableData.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage).map((item, index) => (
              <TableRow
                key={`captive-portal-table-row-${index}`}
                sx={{
                  position: 'relative',
                  backgroundColor: index % 2 === 1 ? theme.palette.primary.whiteSecound : theme.palette.primary.white,
                  cursor: 'pointer',
                  boxShadow: selectedRowItem?.id === item?.id ? `0 0 0 2px ${theme.palette.primary.greenPrimary} inset` : '',
                }}
                onClick={() => handleClickRow(item)}
              >
                {showEditGeneral && (
                  <TableCell>
                    <Checkbox
                      icon={<CheckBoxDefault style={{ width: '24px', height: '24px' }} />}
                      checkedIcon={<CheckBoxFilled style={{ width: '24px', height: '24px' }} />}
                      checked={item.checked}
                      onClick={e => e.stopPropagation()}
                      onChange={e => handleChangeCheckbox('NOT_CHECK_ALL', e.target.checked, item.id)}
                      sx={{
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Box>{item.captivePortalName}</Box>
                </TableCell>
                <TableCell>
                  <Box>{renderLabel(item.authTypeLabel, item.authType)}</Box>
                </TableCell>
                <TableCell>
                  {item?.config?.authType !== 'MAC_ADDRESS' && item?.config?.authType !== 'EXTERNAL_CAPTIVE_PORTAL' && (
                    <Box>
                      {/* Customize the captive portal page. (Splash page editor) */}
                      <IconButton
                        sx={{
                          '& .hoverIcon': {
                            display: 'none',
                          },
                          '&:hover .defaultIcon': {
                            display: 'none',
                          },
                          '&:hover .hoverIcon': {
                            display: 'inline-flex',
                          },
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          setOpenEditorModal({ mode: 'edit', open: true });
                          setSelectedProfileItem(item);
                        }}
                      >
                        <WriteIcon className="defaultIcon" width="20" height="20" />
                        <WriteHoverIcon className="hoverIcon" width="20" height="20" />
                      </IconButton>
                      {/* Open the preview modal */}
                      <IconButton
                        sx={{
                          '& .hoverIcon': {
                            display: 'none',
                          },
                          '&:hover .defaultIcon': {
                            display: 'none',
                          },
                          '&:hover .hoverIcon': {
                            display: 'inline-flex',
                          },
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          setOpenEditorModal({ mode: 'preview', open: true });
                          setSelectedProfileItem(item);
                        }}
                      >
                        <LaunchIcon className="defaultIcon" width="24" height="24" />
                        <LaunchHoverIcon className="hoverIcon" width="24" height="24" />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={showEditGeneral ? 4 : 3} sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CaptivePortalTable;
